import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface DefinitionItem {
  /** A string, or a label with a note under it; a non-string label should carry its own `key`. */
  label: ReactNode;
  value: ReactNode;
  key?: string;
}

export interface DefinitionListProps {
  items: readonly DefinitionItem[];
  className?: string;
}

/** The label/value grid the Specifications tab is built from. */
export const DefinitionList = ({ items, className }: DefinitionListProps) => (
  <dl className={cn('divide-y divide-border', className)}>
    {items.map((item, index) => (
      <div
        key={item.key ?? (typeof item.label === 'string' ? item.label : `item-${index}`)}
        className="grid grid-cols-1 gap-1 py-2.5 sm:grid-cols-3 sm:gap-4"
      >
        <dt className="text-sm text-muted-foreground">{item.label}</dt>
        <dd className="min-w-0 text-sm text-foreground sm:col-span-2">{item.value}</dd>
      </div>
    ))}
  </dl>
);

export interface StatCardProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}

/** The summary card used on the Overview tab and the dashboard counter grid. */
export const StatCard = ({ label, value, hint }: StatCardProps) => (
  <div className="rounded-lg border border-border bg-card p-4">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-2 text-xl font-semibold text-card-foreground">{value}</p>
    {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);
